import locales from '../content.json';
import { PAYMENT_TARIFF } from '../types';
import { Environment } from '../types/environment';
import { getParameterFromURL } from '../utils/scripts';
import { getPaymentCurrency } from '../utils/scripts/currency';
import { getContentIds } from '../utils/scripts/getContentIds';
import { DEFAULT_DISCOUNT, SCRATCH_DISCOUNT, isSpecialOfferTariff } from './discountConfig';
// const lang = window.navigator.language.slice(0, 2);
let lang = window.navigator.language.slice(0, 2) || 'en';
const supportedLangs = ['en', 'ru', 'es', 'fr', 'de', 'pt', 'it', 'tr', 'pl', 'uk'];
if (!supportedLangs.includes(lang)) {
  lang = 'en';
}
let isContentIdsCalled = false;
let previousTariff: PAYMENT_TARIFF | null = null;

const subscrUK = getParameterFromURL('subscrUK') === 'true';
const subscrEU = getParameterFromURL('subscrEU') === 'true';
const scratchDiscountScreen = getParameterFromURL('p20v1DiscountScreenScratch') === 'true';
const currency = getPaymentCurrency();

const extraOptionsVariants = {
  mostPopularTitle: locales[lang].tariff_most_popular,
};

const durationVariants = {
  oneYear: {
    title: locales[lang].tariff_one_year,
    perFirst: locales[lang].tariff_per_year,
    perSecond: locales[lang].tariff_per_day,
    week: locales[lang].payment_text_65,
  },
  oneMonth: {
    title: locales[lang].tariff_one_month,
    perFirst: locales[lang].tariff_month,
    perSecond: locales[lang].tariff_per_day,
    perWeek: locales[lang].tariff_per_week,
    week: locales[lang].payment_text_66,
  },
  oneWeek: {
    title: locales[lang].tariff_one_week,
    perFirst: locales[lang].tariff_week,
    perSecond: locales[lang].tariff_per_day,
    week: locales[lang].payment_text_117,
  },
  threeMonth: {
    title: locales[lang].tariff_three_month,
    perWeek: locales[lang].tariff_per_week,
    perSecond: locales[lang].tariff_per_day,
    week: locales[lang].payment_text_67,
  },
  sixMonths: {
    title: locales[lang].tariff_six_month,
    perWeek: locales[lang].tariff_per_week,
    perSecond: locales[lang].tariff_per_day,
    week: locales[lang].payment_text_227,
  },
};

const { mostPopularTitle } = extraOptionsVariants;
const { oneYear, oneMonth, oneWeek, threeMonth, sixMonths } = durationVariants;

function getPriceBeforeDiscount(price?: string | number, discountPercent?: string | number) {
  const normalizedPrice = Number(price);
  const normalizedDiscountPercent = Number(discountPercent);

  if (
    !Number.isFinite(normalizedPrice) ||
    !Number.isFinite(normalizedDiscountPercent) ||
    normalizedDiscountPercent <= 0 ||
    normalizedDiscountPercent >= 100
  ) {
    return '';
  }

  return (normalizedPrice / (1 - normalizedDiscountPercent / 100)).toFixed(2);
}

interface IProductIds {
  year?: number;
  month?: number;
  week?: number;
  threeMonths?: number;
  sixMonts?: number;
  one?: number;
  two?: number;
  three?: number;
}

type DefaultPrices = {
  year?: string | number;
  month?: string | number;
  week?: string | number;
  threeMonths?: string | number;
  sixMonts?: string | number;
  defaultOldPrices?: string | number;
  defaultOldPricesForPeriod?: string | number;
  defaultOldPricesPerDay?: string | number;
  productName?: string;
  one?: string | number;
  two?: string | number;
  three?: string | number;
};

interface PricesConfig {
  extraOptions: {
    mostPopularTitle: string;
    discountPercent?: string;
  };
  defaultDesign: {
    firstPriceConfig: any;
    secondPriceConfig: any;
    thirdPriceConfig: any;
  };
  everydoggyV1Design: {
    firstPriceConfig: any;
    secondPriceConfig: any;
    thirdPriceConfig: any;
  };
  everydoggyV2Design: {
    firstPriceConfig: any;
    secondPriceConfig: any;
    thirdPriceConfig: any;
  };
  everydoggyV4Design: {
    firstPriceConfig: any;
    secondPriceConfig: any;
    thirdPriceConfig: any;
  };
  everydoggyV6Design: {
    firstPriceConfig: any;
    secondPriceConfig: any;
    thirdPriceConfig: any;
  };
  everydoggyNewDesign?: {
    firstPriceConfig: any;
    secondPriceConfig: any;
    thirdPriceConfig: any;
  };
}

export function getPricesConfig(tariff: PAYMENT_TARIFF, environment = process.env.ENVIRONMENT) {
  let ids: IProductIds;
  let title: DefaultPrices;
  let defaultPrices: DefaultPrices;
  let defaultPricesPerDay: DefaultPrices;
  let defaultPricesPerWeek: DefaultPrices;
  let defaultOldPrices: DefaultPrices;
  let defaultOldPricesForPeriod: DefaultPrices;
  let defaultOldPricesPerDay: DefaultPrices;
  let productName: DefaultPrices;
  let discountPercent: string | undefined;

  switch (tariff) {
    case PAYMENT_TARIFF.DEFAULT:
      ids = {
        year: environment === Environment.prod ? 3 : 7,
        month: environment === Environment.prod ? 2 : 9,
        week: environment === Environment.prod ? 4 : 10,
      };
      defaultPrices = {
        year: '59.99',
        month: '19.99',
        week: '9.99',
      };
      defaultPricesPerDay = {
        year: 0.16,
        month: 0.64,
        week: 1.29,
      };
      defaultPricesPerWeek = {
        year: 7.0,
        month: 3.11,
        week: 1.94,
      };
      break;
    case PAYMENT_TARIFF.V1:
      ids = {
        year: environment === Environment.prod ? 9 : 13,
        month: environment === Environment.prod ? 8 : 12,
        week: environment === Environment.prod ? 7 : 11,
      };
      defaultPrices = {
        year: '69.99',
        month: '29.99',
        week: '19.99',
      };
      defaultPricesPerDay = {
        year: 0.19,
        month: 0.97,
        week: 2.58,
      };
      break;
    case PAYMENT_TARIFF.V2:
      ids = {
        month: environment === Environment.prod ? 8 : 12,
        threeMonths: environment === Environment.prod ? 10 : 15,
        sixMonts: environment === Environment.prod ? 11 : 14,
      };
      defaultPrices = {
        month: '29.99',
        threeMonths: '39.99',
        sixMonts: '49.99',
      };
      defaultPricesPerDay = {
        month: 0.99,
        threeMonths: 0.44,
        sixMonts: 0.28,
      };
      defaultPricesPerWeek = {
        month: 6.99,
        threeMonths: 2.99,
        sixMonts: 1.99,
      };
      break;
    case PAYMENT_TARIFF.V3:
      ids = {
        month: environment === Environment.prod ? 12 : 7,
        threeMonths: environment === Environment.prod ? 13 : 13,
        sixMonts: environment === Environment.prod ? 14 : 10,
      };
      defaultPrices = {
        month: '14.99',
        threeMonths: '19.99',
        sixMonts: '24.99',
      };
      defaultPricesPerDay = {
        month: 1,
        threeMonths: 2,
        sixMonts: 3,
      };
      defaultPricesPerWeek = {
        month: 3.49,
        threeMonths: 1.55,
        sixMonts: 0.97,
      };
      break;
    
    // p1
    case PAYMENT_TARIFF.V4:
      ids = {
        year: environment === Environment.prod ? 9 : 13,
        month: environment === Environment.prod ? 8 : 12,
        threeMonths: environment === Environment.prod ? 10 : 15,
      };

      defaultPrices = {
        year: '69.99',
        month: '29.99',
        threeMonths: '39.99',
      };
      defaultPricesPerDay = {
        year: 0.19,
        month: 0.97,
        threeMonths: 0.44,
      };
      defaultPricesPerWeek = {
        year: 1.36,
        month: 6.99,
        threeMonths: 3.11,
      };

      productName = {
        year: '1 year for $69.99',
        month: '1 month plan for $29.99',
        threeMonths: '3 months plan for $39.99',
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '2.88',
        two: '14.66',
        three: '6.66',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPrices = {
        year: '148.44',
        month: '62.82',
        threeMonths: '85.68',
      };

      break;
    case PAYMENT_TARIFF.V5:
      ids = {
        year: environment === Environment.prod ? 17 : 18,
        month: environment === Environment.prod ? 15 : 16,
        threeMonths: environment === Environment.prod ? 16 : 17,
      };
      defaultPrices = {
        year: '51.99',
        month: '21.99',
        threeMonths: '29.99',
      };
      defaultPricesPerDay = {
        year: 0.14,
        month: 0.73,
        threeMonths: 0.33,
      };
      defaultPricesPerWeek = {
        year: 1.01,
        month: 5.13,
        threeMonths: 2.33,
      };
      defaultOldPrices = {
        year: '148.44',
        month: '62.82',
        threeMonths: '85.68',
      };
      productName = {
        year: '1 year for $51.99',
        month: '1 month plan for $21.99',
        threeMonths: '3 months plan for $29.99',
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '2.88',
        two: '14.66',
        three: '6.66',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPrices = {
        year: '148.44',
        month: '62.82',
        threeMonths: '85.68',
      };

      break;
    
    case PAYMENT_TARIFF.V6:
      ids = {
        year: environment === Environment.prod ? 21 : 22,
        month: environment === Environment.prod ? 18 : 19,
        threeMonths: environment === Environment.prod ? 20 : 21,
      };
      defaultPrices = {
        year: '79.99',
        month: '39.99',
        threeMonths: '59.99',
      };
      defaultPricesPerDay = {
        year: 0.19,
        month: 1.33,
        threeMonths: 0.44,
      };
      defaultPricesPerWeek = {
        year: 3.49,
        month: 9.33,
        threeMonths: 0.97,
      };
      defaultOldPrices = {
        year: '148.44',
        month: '62.82',
        threeMonths: '85.68',
      };
      productName = {
        year: '1 year for $79.99',
        month: '1 month plan for $39.99',
        threeMonths: '3 months plan for $59.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    case PAYMENT_TARIFF.V7:
      ids = {
        year: environment === Environment.prod ? 17 : 18,
        month: environment === Environment.prod ? 8 : 12,
        threeMonths: environment === Environment.prod ? 10 : 15,
      };
      defaultPrices = {
        year: '51.99',
        month: '29.99',
        threeMonths: '39.99',
      };
      defaultPricesPerDay = {
        year: 0.14,
        month: 0.97,
        threeMonths: 0.33,
      };
      defaultPricesPerWeek = {
        year: 3.49,
        month: 1.55,
        threeMonths: 0.97,
      };
      defaultOldPrices = {
        year: '148.44',
        month: '62.82',
        threeMonths: '85.68',
      };
      productName = {
        year: '1 year for $51.99',
        month: '1 month plan for $29.99',
        threeMonths: '3 months plan for $39.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    case PAYMENT_TARIFF.V8:
      ids = {
        year: environment === Environment.prod ? 9 : 13,
        month: environment === Environment.prod ? 2 : 9,
        threeMonths: environment === Environment.prod ? 10 : 15,
      };
      defaultPrices = {
        year: '69.99',
        month: '19.99',
        threeMonths: '39.99',
      };
      defaultPricesPerDay = {
        year: 0.19,
        month: 0.64,
        threeMonths: 0.44,
      };
      defaultPricesPerWeek = {
        year: 3.49,
        month: 4.66,
        threeMonths: 0.97,
      };
      defaultOldPrices = {
        year: '148.44',
        month: '51.40',
        threeMonths: '85.68',
      };
      productName = {
        year: '1 year for $69.99',
        month: '1 month plan for $19.99',
        threeMonths: '3 months plan for $39.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    case PAYMENT_TARIFF.V9:
      ids = {
        year: environment === Environment.prod ? 17 : 18,
        month: environment === Environment.prod ? 19 : 20,
        threeMonths: environment === Environment.prod ? 16 : 17,
      };
      defaultPrices = {
        year: '51.99',
        month: '17.99',
        threeMonths: '29.99',
      };
      defaultPricesPerDay = {
        year: 0.14,
        month: 0.59,
        threeMonths: 0.33,
      };
      defaultPricesPerWeek = {
        year: 3.49,
        month: 4.19,
        threeMonths: 0.97,
      };
      defaultOldPrices = {
        year: '148.44',
        month: '51.40',
        threeMonths: '85.68',
      };
      productName = {
        year: '1 year for $51.99',
        month: '1 month plan for $17.99',
        threeMonths: '3 months plan for $29.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    case PAYMENT_TARIFF.V10:
      ids = {
        year: environment === Environment.prod ? 21 : 22,
        month: environment === Environment.prod ? 22 : 23,
        threeMonths: environment === Environment.prod ? 23 : 24,
      };
      defaultPrices = {
        year: '79.99',
        month: '34.99',
        threeMonths: '47.99',
      };
      defaultPricesPerDay = {
        year: 0.22,
        month: 1.16,
        threeMonths: 0.53,
      };
      defaultPricesPerWeek = {
        year: 1.54,
        month: 8.12,
        threeMonths: 3.71,
      };
      defaultOldPrices = {
        year: '199.97',
        month: '85.68',
        threeMonths: '114.26',
      };
      productName = {
        year: '1 year for $79.99',
        month: '1 month plan for $34.99',
        threeMonths: '3 months plan for $47.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    case PAYMENT_TARIFF.V11:
      ids = {
        year: environment === Environment.prod ? 9 : 13,
        month: environment === Environment.prod ? 8 : 12,
        threeMonths: environment === Environment.prod ? 10 : 15,
      };
      defaultPrices = {
        year: '69.99',
        month: '29.99',
        threeMonths: '39.99',
      };
      defaultPricesPerDay = {
        year: 0.19,
        month: 0.99,
        threeMonths: 0.44,
      };
      defaultPricesPerWeek = {
        year: 1.33,
        month: 6.99,
        threeMonths: 3.11,
      };
      defaultOldPrices = {
        year: '199.97',
        month: '85.68',
        threeMonths: '114.26',
      };
      productName = {
        year: '1 year for $69.99',
        month: '1 month plan for $29.99',
        threeMonths: '3 months plan for $39.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    case PAYMENT_TARIFF.V12:
      ids = {
        year: environment === Environment.prod ? 24 : 26,
        month: environment === Environment.prod ? 25 : 25,
        threeMonths: environment === Environment.prod ? 16 : 17,
      };
      defaultPrices = {
        year: '54.99',
        month: '23.99',
        threeMonths: '29.99',
      };
      defaultPricesPerDay = {
        year: 0.15,
        month: 0.79,
        threeMonths: 0.33,
      };
      defaultPricesPerWeek = {
        year: 1.05,
        month: 5.53,
        threeMonths: 2.31,
      };
      defaultOldPrices = {
        year: '114.26',
        month: '48.54',
        threeMonths: '68.54',
      };
      productName = {
        year: '1 year for $54.99',
        month: '1 month plan for $23.99',
        threeMonths: '3 months plan for $29.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    case PAYMENT_TARIFF.V13:
      ids = {
        year: environment === Environment.prod ? 26 : 27,
        month: environment === Environment.prod ? 27 : 28,
        threeMonths: environment === Environment.prod ? 28 : 29,
      };
      defaultPrices = {
        year: '39.99',
        month: '16.99',
        threeMonths: '23.99',
      };
      defaultPricesPerDay = {
        year: 0.11,
        month: 0.56,
        threeMonths: 0.26,
      };
      defaultPricesPerWeek = {
        year: 0.77,
        month: 3.92,
        threeMonths: 1.82,
      };
      defaultOldPrices = {
        year: '114.26',
        month: '48.54',
        threeMonths: '68.54',
      };
      productName = {
        year: '1 year for $39.99',
        month: '1 month plan for $16.99',
        threeMonths: '3 months plan for $23.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    case PAYMENT_TARIFF.V14:
      ids = {
        year: environment === Environment.prod ? 9 : 13,
        month: environment === Environment.prod ? 32 : 36,
        threeMonths: environment === Environment.prod ? 33 : 37,
      };
      defaultPrices = {
        year: '69.99',
        month: '31.99',
        threeMonths: '43.99',
      };
      defaultPricesPerDay = {
        year: 0.19,
        month: 1.06,
        threeMonths: 0.49,
      };
      defaultPricesPerWeek = {
        year: 1.33,
        month: 7.42,
        threeMonths: 3.43,
      };
      defaultOldPrices = {
        year: '148.44',
        month: '68.54',
        threeMonths: '91.49',
      };
      productName = {
        year: '1 year for $69.99',
        month: '1 month plan for $31.99',
        threeMonths: '3 months plan for $43.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    case PAYMENT_TARIFF.V15:
      ids = {
        year: environment === Environment.prod ? 17 : 18,
        month: environment === Environment.prod ? 25 : 25,
        threeMonths: environment === Environment.prod ? 34 : 38,
      };
      defaultPrices = {
        year: '51.99',
        month: '23.99',
        threeMonths: '31.99',
      };
      defaultPricesPerDay = {
        year: 0.19,
        month: 0.79,
        threeMonths: 0.35,
      };
      defaultPricesPerWeek = {
        year: 1.33,
        month: 2.37,
        threeMonths: 2.45,
      };
      defaultOldPrices = {
        year: '148.44',
        month: '68.54',
        threeMonths: '91.49',
      };
      productName = {
        year: '1 year for $51.99',
        month: '1 month plan for $23.99',
        threeMonths: '3 months plan for $31.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    case PAYMENT_TARIFF.V16:
      ids = {
        week: environment === Environment.prod ? 29 : 30,
        month: environment === Environment.prod ? 30 : 34,
        threeMonths: environment === Environment.prod ? 31 : 35,
      };
      defaultPrices = {
        week: '6.99',
        month: '19.99',
        threeMonths: '29.99',
      };
      defaultPricesPerDay = {
        week: 0.99,
        month: 0.66,
        threeMonths: 0.33,
      };
      defaultPricesPerWeek = {
        week: 1.33,
        month: 6.99,
        threeMonths: 3.11,
      };
      defaultOldPrices = {
        week: '13.99',
        month: '39.99',
        threeMonths: '59.99',
      };
      productName = {
        week: '$6.99 per 1w intro, then $29.99 per month',
        month: '$19.99 per 1m intro, then $29.99 per month',
        threeMonths: '$29.99 per 3m intro, then $39.99 per 3 months',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    case PAYMENT_TARIFF.V17:
      ids = {
        week: environment === Environment.prod ? 35 : 39,
        month: environment === Environment.prod ? 8 : 12,
        threeMonths: environment === Environment.prod ? 10 : 15,
      };
      defaultPrices = {
        week: '9.99',
        month: '29.99',
        threeMonths: '39.99',
      };
      defaultPricesPerDay = {
        week: 1.42,
        month: 0.97,
        threeMonths: 0.44,
      };
      defaultPricesPerWeek = {
        week: 9.99,
        month: 6.99,
        threeMonths: 3.11,
      };
      defaultOldPrices = {
        week: '21.26',
        month: '62.82',
        threeMonths: '85.68',
      };
      productName = {
        week: '1 week for $9.99',
        month: '1 month plan for $29.99',
        threeMonths: '3 months plan for $39.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    case PAYMENT_TARIFF.V18:
      ids = {
        week: environment === Environment.prod ? 36 : 40,
        month: environment === Environment.prod ? 15 : 16,
        threeMonths: environment === Environment.prod ? 16 : 17,
      };
      defaultPrices = {
        week: '7.99',
        month: '21.99',
        threeMonths: '29.99',
      };
      defaultPricesPerDay = {
        week: 1.14,
        month: 0.73,
        threeMonths: 0.33,
      };
      defaultPricesPerWeek = {
        week: 7.99,
        month: 5.13,
        threeMonths: 2.33,
      };
      defaultOldPrices = {
        week: '21.26',
        month: '62.82',
        threeMonths: '85.68',
      };
      productName = {
        week: '1 week for $7.99',
        month: '1 month plan for $21.99',
        threeMonths: '3 months plan for $29.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    case PAYMENT_TARIFF.V19:
      ids = {
        week: environment === Environment.prod ? 37 : 43,
        month: environment === Environment.prod ? 8 : 12,
        threeMonths: environment === Environment.prod ? 10 : 15,
      };
      defaultPrices = {
        week: '12.99',
        month: '29.99',
        threeMonths: '39.99',
      };
      defaultPricesPerDay = {
        week: 1.42,
        month: 0.97,
        threeMonths: 0.44,
      };
      defaultPricesPerWeek = {
        week: 9.99,
        month: 6.99,
        threeMonths: 3.11,
      };
      defaultOldPrices = {
        week: '27.64',
        month: '62.82',
        threeMonths: '85.68',
      };
      productName = {
        week: '1 week for $12.99',
        month: '1 month plan for $29.99',
        threeMonths: '3 months plan for $39.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    case PAYMENT_TARIFF.V20:
      ids = {
        week: environment === Environment.prod ? 35 : 39,
        month: environment === Environment.prod ? 15 : 16,
        threeMonths: environment === Environment.prod ? 16 : 17,
      };
      defaultPrices = {
        week: '9.99',
        month: '21.99',
        threeMonths: '29.99',
      };
      defaultPricesPerDay = {
        week: 1.14,
        month: 0.73,
        threeMonths: 0.33,
      };
      defaultPricesPerWeek = {
        week: 7.99,
        month: 5.13,
        threeMonths: 2.33,
      };
      defaultOldPrices = {
        week: '27.64',
        month: '62.82',
        threeMonths: '85.68',
      };
      productName = {
        week: '1 week for $9.99',
        month: '1 month plan for $21.99',
        threeMonths: '3 months plan for $29.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;

    // p10
    case PAYMENT_TARIFF.V21:
      ids = {
        one: environment === Environment.prod ? 40 : 31,
        two: environment === Environment.prod ? 41 : 32,
        three: environment === Environment.prod ? 42 : 33,
      };
      defaultPrices = {
        one: '6.99',
        two: '19.99',
        three: '29.99',
      };
      title = {
        one: oneWeek.week,
        two: oneMonth.week,
        three: threeMonth.week,
      };
      defaultPricesPerDay = {
        one: 0.99,
        two: 0.66,
        three: 0.33,
      };
      defaultPricesPerWeek = {
        one: 6.99,
        two: 4.99,
        three: 2.49,
      };
      productName = {
        one: '$6.99 per 1w intro, then $39.99 per month',
        two: '$19.99 per 1m intro, then $39.99 per month',
        three: '$29.99 per 3m intro, then $59.99 per 3 months',
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '14.87',
        two: '9.91',
        three: '4.95',
      };
      defaultOldPricesPerDay = {
        one: '2.11',
        two: '1.40',
        three: '0.70',
      };
      defaultOldPrices = {
        one: '14.87',
        two: '62.82',
        three: '68.40',
      };

      break;
    case PAYMENT_TARIFF.V22:
      ids = {
        one: environment === Environment.prod ? 43 : 46,
        two: environment === Environment.prod ? 44 : 47,
        three: environment === Environment.prod ? 45 : 48,
      };
      title = {
        one: oneWeek.week,
        two: oneMonth.week,
        three: threeMonth.week,
      };
      defaultPrices = {
        one: '4.99',
        two: '14.99',
        three: '19.99',
      };
      defaultPricesPerDay = {
        one: 0.71,
        two: 0.49,
        three: 0.22,
      };
      defaultPricesPerWeek = {
        one: 4.99,
        two: 3.74,
        three: 1.66,
      };
      productName = {
        one: '$4.99 per 1w intro, then $39.99 per month',
        two: '$14.99 per 1m intro, then $39.99 per month',
        three: '$19.99 per 3m intro, then $59.99 per 3 months',
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '14.87',
        two: '9.91',
        three: '4.95',
      };
      defaultOldPricesPerDay = {
        one: '2.11',
        two: '1.40',
        three: '0.70',
      };
      defaultOldPrices = {
        one: '14.87',
        two: '62.82',
        three: '68.40',
      };

      break;

    case PAYMENT_TARIFF.V23:
      ids = {
        sixMonts: environment === Environment.prod ? 54 : 58,
        month: environment === Environment.prod ? 52 : 56,
        threeMonths: environment === Environment.prod ? 53 : 57,
      };
      defaultPrices = {
        sixMonts: '149.99',
        month: '59.99',
        threeMonths: '101.99',
      };
      defaultPricesPerDay = {
        sixMonts: 0.8,
        month: 1.99,
        threeMonths: 1.13,
      };
      defaultPricesPerWeek = {
        sixMonts: 5.83,
        month: 14,
        threeMonths: 7.93,
      };
      defaultOldPrices = {
        sixMonts: '187.49',
        month: '74.99',
        threeMonths: '127.49',
      };
      productName = {
        sixMonts: '6 months plan for $149.99',
        month: '1 month plan for $59.99',
        threeMonths: '3 months plan for $101.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    case PAYMENT_TARIFF.V24:
      ids = {
        sixMonts: environment === Environment.prod ? 57 : 61,
        month: environment === Environment.prod ? 55 : 59,
        threeMonths: environment === Environment.prod ? 56 : 60,
      };
      defaultPrices = {
        sixMonts: '111.99',
        month: '44.99',
        threeMonths: '75.99',
      };
      defaultPricesPerDay = {
        sixMonts: 0.6,
        month: 1.49,
        threeMonths: 0.84,
      };
      defaultPricesPerWeek = {
        sixMonts: 4.35,
        month: 10.49,
        threeMonths: 5.91,
      };
      defaultOldPrices = {
        sixMonts: '139.99',
        month: '56.24',
        threeMonths: '94.99',
      };
      productName = {
        sixMonts: '6 months plan for $111.99',
        month: '1 month plan for $44.99',
        threeMonths: '3 months plan for $75.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    case PAYMENT_TARIFF.V25:
      ids = {
        year: environment === Environment.prod ? 48 : 52,
        month: environment === Environment.prod ? 46 : 50,
        threeMonths: environment === Environment.prod ? 47 : 51,
      };
      defaultPrices = {
        year: '55.99',
        month: '23.99',
        threeMonths: '31.99',
      };
      defaultPricesPerDay = {
        year: 0.15,
        month: 0.79,
        threeMonths: 0.35,
      };
      defaultPricesPerWeek = {
        year: 1.08,
        month: 5.59,
        threeMonths: 2.48,
      };
      defaultOldPrices = {
        year: '69.99',
        month: '29.99',
        threeMonths: '39.99',
      };
      productName = {
        year: '1 year for £55.99',
        month: '1 month plan for £23.99',
        threeMonths: '3 months plan for £31.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    case PAYMENT_TARIFF.V26:
      ids = {
        year: environment === Environment.prod ? 51 : 55,
        month: environment === Environment.prod ? 49 : 53,
        threeMonths: environment === Environment.prod ? 50 : 54,
      };
      defaultPrices = {
        year: '41.99',
        month: '16.99',
        threeMonths: '23.99',
      };
      defaultPricesPerDay = {
        year: 0.11,
        month: 0.56,
        threeMonths: 0.26,
      };
      defaultPricesPerWeek = {
        year: 0.81,
        month: 3.96,
        threeMonths: 1.86,
      };
      defaultOldPrices = {
        year: '52.49',
        month: '21.24',
        threeMonths: '29.99',
      };
      productName = {
        year: '1 year for £41.99',
        month: '1 month plan for £16.99',
        threeMonths: '3 months plan for £23.99',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
      break;
    
    // p11
    case PAYMENT_TARIFF.V27:
      ids = {
        one: environment === Environment.prod ? 58 : 62,
        two: environment === Environment.prod ? 59 : 63,
        three: environment === Environment.prod ? 60 : 64,
      };
      title = {
        one: oneMonth.week,
        two: threeMonth.week,
        three: sixMonths.week,
      };
      defaultPrices = {
        one: '15.19',
        two: '25.99',
        three: '37.99',
      };
      defaultPricesPerDay = {
        one: 0.5,
        two: 0.28,
        three: 0.21,
      };
      defaultPricesPerWeek = {
        one: 3.5,
        two: 1.96,
        three: 1.47,
      };
      defaultOldPrices = {
        one: '32.32',
        two: '55.29',
        three: '80.83',
      };
      productName = {
        one: '$15.19 per 1m intro, then $49.99 per month',
        two: '$25.99 per 3m intro, then $79.99 per 3 months',
        three: '$37.99 per 6m intro, then $119.99 per 6 months',
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '7.53',
        two: '4.30',
        three: '3.13',
      };
      defaultOldPricesPerDay = {
        one: '1.06',
        two: '0.60',
        three: '0.45',
      };
      defaultOldPrices = {
        one: '32.32',
        two: '55.29',
        three: '80.83',
      };

      break;
    case PAYMENT_TARIFF.V28:
      ids = {
        one: environment === Environment.prod ? 61 : 65,
        two: environment === Environment.prod ? 62 : 66,
        three: environment === Environment.prod ? 63 : 67,
      };
      title = {
        one: oneMonth.week,
        two: threeMonth.week,
        three: sixMonths.week,
      };
      defaultPrices = {
        one: '11.69',
        two: '19.99',
        three: '28.79',
      };
      defaultPricesPerDay = {
        one: 0.71,
        two: 0.49,
        three: 0.22,
      };
      defaultPricesPerWeek = {
        one: 4.99,
        two: 3.74,
        three: 1.66,
      };
      productName = {
        one: '$11.69 per 1m intro, then $49.99 per month',
        two: '$19.99 per 3m intro, then $79.99 per 3 months',
        three: '$28.79 per 6m intro, then $119.99 per 6 months',
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '7.53',
        two: '4.30',
        three: '3.13',
      };
      defaultOldPricesPerDay = {
        one: '1.06',
        two: '0.60',
        three: '0.45',
      };
      defaultOldPrices = {
        one: '32.32',
        two: '55.29',
        three: '80.83',
      };

      break;
    
    // p14
    case PAYMENT_TARIFF.V29:
      ids = {
        one: environment === Environment.prod ? 40 : 31,
        two: environment === Environment.prod ? 44 : 47,
        three: environment === Environment.prod ? 42 : 33,
      };
      defaultPrices = {
        one: '6.99',
        two: '14.99',
        three: '29.99',
      };
      title = {
        one: oneWeek.week,
        two: oneMonth.week,
        three: threeMonth.week,
      };
      defaultPricesPerDay = {
        one: 0.99,
        two: 0.49,
        three: 0.33,
      };
      defaultPricesPerWeek = {
        one: 6.99,
        two: 3.49,
        three: 2.33,
      };
      productName = {
        one: '$6.99 per 1w intro, then $39.99 per month',
        two: '$14.99 per 1m intro, then $39.99 per month',
        three: '$29.99 per 3m intro, then $59.99 per 3 months',
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '14.87',
        two: '7.43',
        three: '4.96',
      };
      defaultOldPricesPerDay = {
        one: '2.11',
        two: '1.04',
        three: '0.70',
      };
      defaultOldPrices = {
        one: '14.87',
        two: '62.82',
        three: '68.40',
      };

      break;
    case PAYMENT_TARIFF.V30:
      ids = {
        one: environment === Environment.prod ? 43 : 46,
        two: environment === Environment.prod ? 64 : 68,
        three: environment === Environment.prod ? 45 : 48,
      };
      title = {
        one: oneWeek.week,
        two: oneMonth.week,
        three: threeMonth.week,
      };
      defaultPrices = {
        one: '4.99',
        two: '11.99',
        three: '19.99',
      };
      defaultPricesPerDay = {
        one: 0.71,
        two: 0.39,
        three: 0.22,
      };
      defaultPricesPerWeek = {
        one: 4.99,
        two: 2.79,
        three: 1.56,
      };
      productName = {
        one: '$4.99 per 1w intro, then $39.99 per month',
        two: '$11.99 per 1m intro, then $39.99 per month',
        three: '$19.99 per 3m intro, then $59.99 per 3 months',
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '14.87',
        two: '7.43',
        three: '4.96',
      };
      defaultOldPricesPerDay = {
        one: '2.11',
        two: '1.04',
        three: '0.70',
      };
      defaultOldPrices = {
        one: '14.87',
        two: '62.82',
        three: '68.40',
      };

      break;

    // p13
    case PAYMENT_TARIFF.V31:
      ids = {
        one: environment === Environment.prod ? 65 : 69,
        two: environment === Environment.prod ? 66 : 70,
        three: environment === Environment.prod ? 59 : 63,
      };
      defaultPrices = {
        one: '4.99',
        two: '11.99',
        three: '25.99',
      };
      title = {
        one: oneWeek.week,
        two: oneMonth.week,
        three: threeMonth.week,
      };
      defaultPricesPerDay = {
        one: 0.71,
        two: 0.39,
        three: 0.28,
      };
      defaultPricesPerWeek = {
        one: 4.99,
        two: 2.79,
        three: 2.02,
      };
      productName = {
        one: '$4.99 per 1w intro, then $49.99 per month',
        two: '$11.99 per 1m intro, then $49.99 per month',
        three: '$25.99 per 3m intro, then $79.99 per 3 months',
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '10.62',
        two: '5.94',
        three: '4.30',
      };
      defaultOldPricesPerDay = {
        one: '1.06',
        two: '0.60',
        three: '0.45',
      };
      defaultOldPrices = {
        one: '14.87',
        two: '62.82',
        three: '68.40',
      };

      break;
    case PAYMENT_TARIFF.V32:
      ids = {
        one: environment === Environment.prod ? 67 : 71,
        two: environment === Environment.prod ? 68 : 72,
        three: environment === Environment.prod ? 62 : 66,
      };
      title = {
        one: oneWeek.week,
        two: oneMonth.week,
        three: threeMonth.week,
      };
      defaultPrices = {
        one: '3.99',
        two: '8.99',
        three: '19.99',
      };
      defaultPricesPerDay = {
        one: 0.71,
        two: 0.49,
        three: 0.22,
      };
      defaultPricesPerWeek = {
        one: 0.93,
        two: 2.09,
        three: 1.56,
      };
      productName = {
        one: '$3.99 per 1w intro, then $49.99 per month',
        two: '$8.99 per 1m intro, then $49.99 per month',
        three: '$19.99 per 3m intro, then $79.99 per 3 months',
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '10.62',
        two: '5.94',
        three: '4.30',
      };
      defaultOldPricesPerDay = {
        one: '1.06',
        two: '0.60',
        three: '0.45',
      };
      defaultOldPrices = {
        one: '14.87',
        two: '62.82',
        three: '68.40',
      };

      break;

    // p16
    case PAYMENT_TARIFF.V33:
      ids = {
        one: environment === Environment.prod ? 69 : 73,
        two: environment === Environment.prod ? 70 : 74,
        three: environment === Environment.prod ? 71 : 75,
      };
      defaultPrices = {
        one: '4.99',
        two: '11.99',
        three: '25.99',
      };
      title = {
        one: oneWeek.week,
        two: oneMonth.week,
        three: threeMonth.week,
      };
      defaultPricesPerDay = {
        one: 0.71,
        two: 0.39,
        three: 0.29,
      };
      defaultPricesPerWeek = {
        one: 4.99,
        two: 2.79,
        three: 2.02,
      };
      productName = {
        one: '$4.99 per 1w intro, then $59.99 per month',
        two: '$11.99 per 1m intro, then $59.99 per month',
        three: '$25.99 per 3m intro, then $119.99 per 3 months',
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '10.62',
        two: '5.94',
        three: '4.30',
      };
      defaultOldPricesPerDay = {
        one: '1.06',
        two: '0.60',
        three: '0.45',
      };
      defaultOldPrices = {
        one: '14.87',
        two: '62.82',
        three: '68.40',
      };

      break;
    case PAYMENT_TARIFF.V34:
      ids = {
        one: environment === Environment.prod ? 72 : 76,
        two: environment === Environment.prod ? 73 : 77,
        three: environment === Environment.prod ? 74 : 78,
      };
      title = {
        one: oneWeek.week,
        two: oneMonth.week,
        three: threeMonth.week,
      };
      defaultPrices = {
        one: '3.99',
        two: '8.99',
        three: '19.99',
      };
      defaultPricesPerDay = {
        one: 0.57,
        two: 0.29,
        three: 0.22,
      };
      defaultPricesPerWeek = {
        one: 0.93,
        two: 2.09,
        three: 1.55,
      };
      productName = {
        one: '$3.99 per 1w intro, then $59.99 per month',
        two: '$8.99 per 1m intro, then $59.99 per month',
        three: '$19.99 per 3m intro, then $119.99 per 3 months',
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '10.62',
        two: '5.94',
        three: '4.30',
      };
      defaultOldPricesPerDay = {
        one: '1.06',
        two: '0.60',
        three: '0.45',
      };
      defaultOldPrices = {
        one: '14.87',
        two: '62.82',
        three: '68.40',
      };

      break;

    case PAYMENT_TARIFF.V35:
      ids = {
        one: environment === Environment.prod ? 43 : 46,
        two: environment === Environment.prod ? 64 : 68,
        three: environment === Environment.prod ? 75 : 79,
      };
      defaultPrices = {
        one: '4.99',
        two: '11.99',
        three: '25.99',
      };
      title = {
        one: oneWeek.week,
        two: oneMonth.week,
        three: threeMonth.week,
      };
      defaultPricesPerDay = {
        one: 0.71,
        two: 0.39,
        three: 0.29,
      };
      defaultPricesPerWeek = {
        one: 4.99,
        two: 2.79,
        three: 2.02,
      };
      productName = {
        one: '$4.99 per 1w intro, then $39.99 per month',
        two: '$11.99 per 1m intro, then $39.99 per month',
        three: '$25.99 per 3m intro, then $59.99 per 3 months',
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '10.62',
        two: '5.94',
        three: '4.30',
      };
      defaultOldPricesPerDay = {
        one: '1.51',
        two: '0.83',
        three: '0.62',
      };
      defaultOldPrices = {
        one: '10.62',
        two: '25.51',
        three: '55.29',
      };

      break;

    case PAYMENT_TARIFF.V36:
      ids = {
        one: environment === Environment.prod ? 76 : 80,
        two: environment === Environment.prod ? 77 : 81,
        three: environment === Environment.prod ? 45 : 48,
      };
      title = {
        one: oneWeek.week,
        two: oneMonth.week,
        three: threeMonth.week,
      };
      defaultPrices = {
        one: '3.99',
        two: '8.99',
        three: '19.99',
      };
      defaultPricesPerDay = {
        one: 0.57,
        two: 0.29,
        three: 0.22,
      };
      defaultPricesPerWeek = {
        one: 3.99,
        two: 2.09,
        three: 1.56,
      };
      productName = {
        one: '$3.99 per 1w intro, then $39.99 per month',
        two: '$8.99 per 1m intro, then $39.99 per month',
        three: '$19.99 per 3m intro, then $59.99 per 3 months',
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '10.62',
        two: '5.94',
        three: '4.30',
      };
      defaultOldPricesPerDay = {
        one: '1.51',
        two: '0.83',
        three: '0.62',
      };
      defaultOldPrices = {
        one: '10.62',
        two: '25.51',
        three: '55.29',
      };

      break;

    case PAYMENT_TARIFF.V37:

      if (subscrUK) {
        ids = {
          one: environment === Environment.prod ? 110 : 115,
          two: environment === Environment.prod ? 111 : 116,
          three: environment === Environment.prod ? 112 : 117,
        };
        defaultPrices = {
          one: '4.99',
          two: '11.99',
          three: '25.99',
        };
        title = {
          one: oneWeek.week,
          two: oneMonth.week,
          three: threeMonth.week,
        };
        defaultPricesPerDay = {
          one: 0.71,
          two: 0.39,
          three: 0.28,
        };
        defaultPricesPerWeek = {
          one: 4.99,
          two: 2.79,
          three: 2.02,
        };
        productName = {
          one: `${currency}4.99 per 1w intro, then ${currency}49.99 per month`,
          two: `${currency}11.99 per 1m intro, then ${currency}49.99 per month`,
          three: `${currency}25.99 per 3m intro, then ${currency}79.99 per 3 months`,
        };

        // old prices
        defaultOldPricesForPeriod = {
          one: '10.62',
          two: '5.94',
          three: '4.30',
        };
        defaultOldPricesPerDay = {
          one: '1.06',
          two: '0.60',
          three: '0.45',
        };
        defaultOldPrices = {
          one: '14.87',
          two: '62.82',
          three: '68.40',
        };
      } else {
        ids = {
          one: environment === Environment.prod ? 78 : 82,
          two: environment === Environment.prod ? 79 : 83,
          three: environment === Environment.prod ? 80 : 84,
        };
        defaultPrices = {
          one: '1.99',
          two: '6.99',
          three: '14.99',
        };
        title = {
          one: oneWeek.week,
          two: oneMonth.week,
          three: threeMonth.week,
        };
        defaultPricesPerDay = {
          one: 0.28,
          two: 0.23,
          three: 0.16,
        };
        defaultPricesPerWeek = {
          one: 1.99,
          two: 1.63,
          three: 1.16,
        };
        productName = {
          one: '$1.99 per 1w intro, then $49.99 per month',
          two: '$6.99 per 1m intro, then $49.99 per month',
          three: '$14.99 per 3m intro, then $79.99 per 3 months',
        };

        // old prices
        defaultOldPricesForPeriod = {
          one: '4.23',
          two: '3.47',
          three: '2.47',
        };
        defaultOldPricesPerDay = {
          one: '0.59',
          two: '0.49',
          three: '0.34',
        };
        defaultOldPrices = {
          one: '4.23',
          two: '14.87',
          three: '31.89',
        };
      }

      break;

    case PAYMENT_TARIFF.V38:
      if (subscrUK) {
        ids = {
          one: environment === Environment.prod ? 113 : 118,
          two: environment === Environment.prod ? 114 : 119,
          three: environment === Environment.prod ? 115 : 120,
        };
        title = {
          one: oneWeek.week,
          two: oneMonth.week,
          three: threeMonth.week,
        };
        defaultPrices = {
          one: '3.99',
          two: '8.99',
          three: '19.99',
        };
        defaultPricesPerDay = {
          one: 0.71,
          two: 0.49,
          three: 0.22,
        };
        defaultPricesPerWeek = {
          one: 0.93,
          two: 2.09,
          three: 1.56,
        };
        productName = {
          one: `${currency}3.99 per 1w intro, then ${currency}49.99 per month`,
          two: `${currency}8.99 per 1m intro, then ${currency}49.99 per month`,
          three: `${currency}19.99 per 3m intro, then ${currency}79.99 per 3 months`,
        };

        // old prices
        defaultOldPricesForPeriod = {
          one: '10.62',
          two: '5.94',
          three: '4.30',
        };
        defaultOldPricesPerDay = {
          one: '1.06',
          two: '0.60',
          three: '0.45',
        };
        defaultOldPrices = {
          one: '14.87',
          two: '62.82',
          three: '68.40',
        };
      } else {
        ids = {
          one: environment === Environment.prod ? 81 : 85,
          two: environment === Environment.prod ? 83 : 87,
          three: environment === Environment.prod ? 82 : 86,
        };

         title = {
          one: oneWeek.week,
          two: oneMonth.week,
          three: threeMonth.week,
        };
        defaultPrices = {
          one: '0.99',
          two: '3.99',
          three: '9.99',
        };
        defaultPricesPerDay = {
          one: 0.14,
          two: 0.13,
          three: 0.11,
        };
        defaultPricesPerWeek = {
          one: 0.99,
          two: 0.93,
          three: 0.77,
        };
        productName = {
          one: '$0.99 per 1w intro, then $49.99 per month',
          two: '$3.99 per 1m intro, then $49.99 per month',
          three: '$9.99 per 3m intro, then $79.99 per 3 months',
        };

        // old prices 
        defaultOldPricesForPeriod = {
          one: '4.23',
          two: '3.47',
          three: '2.47',
        };
        defaultOldPricesPerDay = {
          one: '0.59',
          two: '0.49',
          three: '0.34',
        };
        defaultOldPrices = {
          one: '4.23',
          two: '14.87',
          three: '31.89',
        };
      }

      break;
      
    // p19
    case PAYMENT_TARIFF.V39:
      ids = {
        one: environment === Environment.prod ? 84 : 88,
        two: environment === Environment.prod ? 85 : 89,
        three: environment === Environment.prod ? 86 : 90,
      };
      defaultPrices = {
        one: '4.99',
        two: '11.99',
        three: '25.99',
      };
      title = {
        one: oneWeek.week,
        two: oneMonth.week,
        three: threeMonth.week,
      };
      defaultPricesPerDay = {
        one: 0.71,
        two: 0.39,
        three: 0.28,
      };
      defaultPricesPerWeek = {
        one: 4.99,
        two: 2.79,
        three: 2.02,
      };
      productName = {
        one: '$4.99 per 1w intro, then $29.99 per 2 weeks',
        two: '$11.99 per 1m intro, then $29.99 per 2 weeks',
        three: '$25.99 per 3m intro, then $49.99 per 1 month',
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '10.62',
        two: '5.94',
        three: '4.30',
      };
      defaultOldPricesPerDay = {
        one: '1.06',
        two: '0.60',
        three: '0.45',
      };
      defaultOldPrices = {
        one: '14.87',
        two: '62.82',
        three: '68.40',
      };

      break;
    case PAYMENT_TARIFF.V40:
      ids = {
        one: environment === Environment.prod ? 87 : 91,
        two: environment === Environment.prod ? 88 : 92,
        three: environment === Environment.prod ? 89 : 93,
      };
      title = {
        one: oneWeek.week,
        two: oneMonth.week,
        three: threeMonth.week,
      };
      defaultPrices = {
        one: '3.99',
        two: '8.99',
        three: '19.99',
      };
      defaultPricesPerDay = {
        one: 0.71,
        two: 0.49,
        three: 0.22,
      };
      defaultPricesPerWeek = {
        one: 0.93,
        two: 2.09,
        three: 1.56,
      };
      productName = {
        one: '$3.99 per 1w intro, then $29.99 per 2 weeks',
        two: '$8.99 per 1m intro, then $29.99 per 2 weeks',
        three: '$19.99 per 3m intro, then $49.99 per 1 month',
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '10.62',
        two: '5.94',
        three: '4.30',
      };
      defaultOldPricesPerDay = {
        one: '1.06',
        two: '0.60',
        three: '0.45',
      };
      defaultOldPrices = {
        one: '14.87',
        two: '62.82',
        three: '68.40',
      };

      break;

    // p20
    case PAYMENT_TARIFF.V41:
      // ids = subscrUK
      // ? {
      //     one: environment === Environment.prod ? 98 : 103,
      //     two: environment === Environment.prod ? 99 : 104,
      //     three: environment === Environment.prod ? 100 : 105,
      //   }
      // : {
      //     one: environment === Environment.prod ? 90 : 94,
      //     two: environment === Environment.prod ? 91 : 95,
      //     three: environment === Environment.prod ? 92 : 96,
      //   };

      if (subscrUK) {
        ids = {
          one: environment === Environment.prod ? 98 : 103,
          two: environment === Environment.prod ? 99 : 104,
          three: environment === Environment.prod ? 100 : 105,
        };
      } else if (subscrEU) {
        ids = {
          one: environment === Environment.prod ? 104 : 109,
          two: environment === Environment.prod ? 105 : 110,
          three: environment === Environment.prod ? 106 : 111,
        };
      } else {
        ids = {
          one: environment === Environment.prod ? 90 : 94,
          two: environment === Environment.prod ? 91 : 95,
          three: environment === Environment.prod ? 92 : 96,
        };
      }

      defaultPrices = {
        one: '1.99',
        two: '6.99',
        three: '14.99',
      };
      title = {
        one: oneWeek.week,
        two: oneMonth.week,
        three: threeMonth.week,
      };
      defaultPricesPerDay = {
        one: 0.28,
        two: 0.23,
        three: 0.16,
      };
      defaultPricesPerWeek = {
        one: 1.99,
        two: 1.63,
        three: 1.16,
      };
      productName = {
        one: `${currency}1.99 per 1w intro, then ${currency}29.99 per 2 weeks`,
        two: `${currency}6.99 per 1m intro, then ${currency}29.99 per 2 weeks`,
        three: `${currency}14.99 per 3m intro, then ${currency}49.99 per 1 month`,
      };

      // old prices (recalculated for the default discount below)
      defaultOldPricesForPeriod = {
        one: '10.62',
        two: '5.94',
        three: '4.30',
      };
      defaultOldPricesPerDay = {
        one: '1.06',
        two: '0.60',
        three: '0.45',
      };
      defaultOldPrices = {
        one: '14.87',
        two: '62.82',
        three: '68.40',
      };

      break;

    // v001 — single-price paywall based on v41: one flat 1-day test subscription ($9.99)
    case PAYMENT_TARIFF.V001:
      ids = {
        // Same catalog product id in both prod and dev/staging
        one: 49,
      };
      title = {
        one: '1 day',
      };
      defaultPrices = {
        one: '9.99',
      };
      defaultPricesPerDay = {
        one: 9.99,
      };
      defaultPricesPerWeek = {
        one: 69.93,
      };
      productName = {
        one: `${currency}9.99 per 1 day`,
      };
      defaultOldPricesForPeriod = {
        one: '',
      };
      defaultOldPricesPerDay = {
        one: '',
      };
      defaultOldPrices = {
        one: '',
      };

      break;

    // v002 — same as v001, but the EUR catalog product; always priced/shown in €
    case PAYMENT_TARIFF.V002:
      ids = {
        // Same catalog product id in both prod and dev/staging
        one: 179,
      };
      title = {
        one: '1 day',
      };
      defaultPrices = {
        one: '9.99',
      };
      defaultPricesPerDay = {
        one: 9.99,
      };
      defaultPricesPerWeek = {
        one: 69.93,
      };
      productName = {
        one: '€9.99 per 1 day',
      };
      defaultOldPricesForPeriod = {
        one: '',
      };
      defaultOldPricesPerDay = {
        one: '',
      };
      defaultOldPrices = {
        one: '',
      };

      break;

    // v003 — same as v001, but the GBP catalog product; always priced/shown in £
    case PAYMENT_TARIFF.V003:
      ids = {
        // Same catalog product id in both prod and dev/staging
        one: 178,
      };
      title = {
        one: '1 day',
      };
      defaultPrices = {
        one: '9.99',
      };
      defaultPricesPerDay = {
        one: 9.99,
      };
      defaultPricesPerWeek = {
        one: 69.93,
      };
      productName = {
        one: '£9.99 per 1 day',
      };
      defaultOldPricesForPeriod = {
        one: '',
      };
      defaultOldPricesPerDay = {
        one: '',
      };
      defaultOldPrices = {
        one: '',
      };

      break;
    case PAYMENT_TARIFF.V42:

      // ids = subscrUK
      // ? {
      //     one: environment === Environment.prod ? 101 : 106,
      //     two: environment === Environment.prod ? 102 : 107,
      //     three: environment === Environment.prod ? 103 : 108,
      //   }
      // : {
      //     one: environment === Environment.prod ? 93 : 97,
      //     two: environment === Environment.prod ? 94 : 98,
      //     three: environment === Environment.prod ? 95 : 99,
      //   };

      if (subscrUK) {
        ids = {
          one: environment === Environment.prod ? 101 : 106,
          two: environment === Environment.prod ? 102 : 107,
          three: environment === Environment.prod ? 103 : 108,
        };
      } else if (subscrEU) {
        ids = {
          one: environment === Environment.prod ? 107 : 112,
          two: environment === Environment.prod ? 108 : 113,
          three: environment === Environment.prod ? 109 : 114,
        };
      } else {
        ids = {
          one: environment === Environment.prod ? 93 : 97,
          two: environment === Environment.prod ? 94 : 98,
          three: environment === Environment.prod ? 95 : 99,
        };
      }

      title = {
        one: oneWeek.week,
        two: oneMonth.week,
        three: threeMonth.week,
      };
      defaultPrices = {
        one: '0.99',
        two: '3.99',
        three: '9.99',
      };
      defaultPricesPerDay = {
        one: 0.14,
        two: 0.13,
        three: 0.11,
      };
      defaultPricesPerWeek = {
        one: 0.99,
        two: 0.94,
        three: 0.77,
      };
      productName = {
        one: `${currency}0.99 per 1w intro, then ${currency}29.99 per 2 weeks`,
        two: `${currency}3.99 per 1m intro, then ${currency}29.99 per 2 weeks`,
        three: `${currency}9.99 per 3m intro, then ${currency}49.99 per 1 month`,
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '10.62',
        two: '5.94',
        three: '4.30',
      };
      defaultOldPricesPerDay = {
        one: '1.06',
        two: '0.60',
        three: '0.45',
      };
      defaultOldPrices = {
        one: '14.87',
        two: '62.82',
        three: '68.40',
      };

      break;

    case PAYMENT_TARIFF.V43:
      if (subscrUK) {
        ids = {
          one: environment === Environment.prod ? 125 : 130,
          two: environment === Environment.prod ? 126 : 131,
          three: environment === Environment.prod ? 127 : 132,
        };
      } else if (subscrEU) {
        ids = {
          one: environment === Environment.prod ? 130 : 135,
          two: environment === Environment.prod ? 131 : 136,
          three: environment === Environment.prod ? 132 : 137,
        };
      } else {
        ids = {
          one: environment === Environment.prod ? 18 : 19,
          two: environment === Environment.prod ? 44 : 47,
          three: environment === Environment.prod ? 96 : 101,
        };
      }

      defaultPrices = {
        one: '39.99',
        two: '14.99',
        three: '39.99',
      };
      title = {
        one: oneMonth.week,
        two: oneMonth.week,
        three: threeMonth.week,
      };
      defaultPricesPerDay = {
        one: 1.33,
        two: 0.49,
        three: 0.44,
      };
      defaultPricesPerWeek = {
        one: 1.99,
        two: 1.63,
        three: 1.16,
      };
      productName = {
        one: `1 month plan for ${currency}39.99`,
        two: `${currency}14.99 per 1m intro, then ${currency}39.99 per month`,
        three: `${currency}39.99 per 3m intro, then ${currency}59.99 per 3 months`,
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '39.99',
        two: '39.99',
        three: '59.99',
      };
      defaultOldPricesPerDay = {
        one: '1.06',
        two: '0.60',
        three: '0.45',
      };
      defaultOldPrices = {
        one: '14.87',
        two: '62.82',
        three: '68.40',
      };

      break;
    case PAYMENT_TARIFF.V44:

      if (subscrUK) {
        ids = {
          one: environment === Environment.prod ? 125 : 130,
          two: environment === Environment.prod ? 128 : 133,
          three: environment === Environment.prod ? 129 : 134,
        };
      } else if (subscrEU) {
        ids = {
          one: environment === Environment.prod ? 130 : 135,
          two: environment === Environment.prod ? 133 : 138,
          three: environment === Environment.prod ? 134 : 139,
        };
      } else {
        ids = {
          one: environment === Environment.prod ? 18 : 19,
          two: environment === Environment.prod ? 64 : 68,
          three: environment === Environment.prod ? 42 : 33,
        };
      }

      title = {
        one: oneMonth.week,
        two: oneMonth.week,
        three: threeMonth.week,
      };
      defaultPrices = {
        one: '39.99',
        two: '11.99',
        three: '29.99',
      };
      defaultPricesPerDay = {
        one: 1.33,
        two: 0.39,
        three: 0.33,
      };
      defaultPricesPerWeek = {
        one: 0.99,
        two: 0.94,
        three: 0.77,
      };
      productName = {
        one: `1 month plan for ${currency}39.99`,
        two: `${currency}11.99 per 1m intro, then ${currency}39.99 per month`,
        three: `${currency}29.99 per 3m intro, then ${currency}59.99 per 3 months`,
      };

      // old prices 
      defaultOldPricesForPeriod = {
        one: '39.99',
        two: '39.99',
        three: '59.99',
      };
      defaultOldPricesPerDay = {
        one: '1.06',
        two: '0.60',
        three: '0.45',
      };
      defaultOldPrices = {
        one: '14.87',
        two: '62.82',
        three: '68.40',
      };

      break;

      //p27
      case PAYMENT_TARIFF.V45:
        if (subscrUK) {
          ids = {
            one: environment === Environment.prod ? 126 : 131,
            two: environment === Environment.prod ? 139 : 144,
            three: environment === Environment.prod ? 140 : 145,
          };
        } else if (subscrEU) {
          ids = {
            one: environment === Environment.prod ? 131 : 136,
            two: environment === Environment.prod ? 143 : 148,
            three: environment === Environment.prod ? 144 : 149,
          };
        } else {
          ids = {
            one: environment === Environment.prod ? 44 : 47,
            two: environment === Environment.prod ? 135 : 140,
            three: environment === Environment.prod ? 136 : 141,
          };
        }

        defaultPrices = {
          one: '14.99',
          two: '25.99',
          three: '37.99',
        };
        title = {
          one: oneMonth.week,
          two: threeMonth.week,
          three: sixMonths.week,
        };
        defaultPricesPerDay = {
          one: 0.39,
          two: 0.22,
          three: 0.16,
        };
        defaultPricesPerWeek = {
          one: 1.99,
          two: 1.63,
          three: 1.16,
        };
        productName = {
          one: `${currency}14.99 per 1m intro, then ${currency}39.99 per month`,
          two: `${currency}25.99 per 3m intro, then ${currency}64.99 per 3 months`,
          three: `${currency}37.99 per 6m intro, then ${currency}94.99 per 6 months`,
        };

        // old prices 
        defaultOldPricesForPeriod = {
          one: '39.99',
          two: '64.99',
          three: '94.99',
        };
        defaultOldPricesPerDay = {
          one: '1.06',
          two: '0.60',
          three: '0.45',
        };
        defaultOldPrices = {
           one: '39.99',
          two: '64.99',
          three: '94.99',
        };

        break;
      case PAYMENT_TARIFF.V46:

        if (subscrUK) {
          ids = {
            one: environment === Environment.prod ? 128 : 133,
            two: environment === Environment.prod ? 141 : 146,
            three: environment === Environment.prod ? 142 : 147,
          };
        } else if (subscrEU) {
          ids = {
            one: environment === Environment.prod ? 133 : 138,
            two: environment === Environment.prod ? 145 : 150,
            three: environment === Environment.prod ? 146 : 151,
          };
        } else {
          ids = {
            one: environment === Environment.prod ? 64 : 68,
            two: environment === Environment.prod ? 137 : 142,
            three: environment === Environment.prod ? 138 : 143,
          };
        }

        title = {
          one: oneMonth.week,
          two: threeMonth.week,
          three: sixMonths.week,
        };
        defaultPrices = {
          one: '11.99',
          two: '19.99',
          three: '29.99',
        };
        defaultPricesPerDay = {
          one: 1.33,
          two: 0.39,
          three: 0.33,
        };
        defaultPricesPerWeek = {
          one: 0.99,
          two: 0.94,
          three: 0.77,
        };
        productName = {
          one: `${currency}11.99 per 1m intro, then ${currency}39.99 per month`,
          two: `${currency}19.99 per 3m intro, then ${currency}64.99 per 3 months`,
          three: `${currency}29.99 per 6m intro, then ${currency}94.99 per 6 months`,
        };

        // old prices 
        defaultOldPricesForPeriod = {
          one: '39.99',
          two: '64.99',
          three: '94.99',
        };
        defaultOldPricesPerDay = {
          one: '1.06',
          two: '0.60',
          three: '0.45',
        };
        defaultOldPrices = {
          one: '39.99',
          two: '64.99',
          three: '94.99',
        };

        break;

      //p29
      case PAYMENT_TARIFF.V47:
        discountPercent = '60';

        if (subscrUK) {
          ids = {
            one: environment === Environment.prod ? 128 : 133,
            two: environment === Environment.prod ? 149 : 154,
            three: environment === Environment.prod ? 150 : 155,
          };
        } else if (subscrEU) {
          ids = {
            one: environment === Environment.prod ? 133 : 138,
            two: environment === Environment.prod ? 151 : 156,
            three: environment === Environment.prod ? 152 : 157,
          };
        } else {
          ids = {
            one: environment === Environment.prod ? 64 : 68,
            two: environment === Environment.prod ? 147 : 152,
            three: environment === Environment.prod ? 148 : 153,
          };
        }

        title = {
          one: oneMonth.week,
          two: threeMonth.week,
          three: sixMonths.week,
        };
        defaultPrices = {
          one: '11.99',
          two: '21.99',
          three: '32.99',
        };
        defaultPricesPerDay = {
          one: 0.39,
          two: 0.24,
          three: 0.18,
        };
        defaultPricesPerWeek = {
          one: 2.79,
          two: 1.71,
          three: 1.28,
        };
        productName = {
          one: `${currency}11.99 per 1m intro, then ${currency}39.99 per month`,
          two: `${currency}21.99 per 3m intro, then ${currency}64.99 per 3 months`,
          three: `${currency}32.99 per 6m intro, then ${currency}94.99 per 6 months`,
        };

        // old prices are derived from the displayed discount percent for V47 only
        defaultOldPricesForPeriod = {
          one: getPriceBeforeDiscount(defaultPricesPerWeek.one, discountPercent),
          two: getPriceBeforeDiscount(defaultPricesPerWeek.two, discountPercent),
          three: getPriceBeforeDiscount(defaultPricesPerWeek.three, discountPercent),
        };
        defaultOldPricesPerDay = {
          one: getPriceBeforeDiscount(defaultPricesPerDay.one, discountPercent),
          two: getPriceBeforeDiscount(defaultPricesPerDay.two, discountPercent),
          three: getPriceBeforeDiscount(defaultPricesPerDay.three, discountPercent),
        };
        defaultOldPrices = {
          one: getPriceBeforeDiscount(defaultPrices.one, discountPercent),
          two: getPriceBeforeDiscount(defaultPrices.two, discountPercent),
          three: getPriceBeforeDiscount(defaultPrices.three, discountPercent),
        };

        break;

      //p30
      case PAYMENT_TARIFF.V48:
        discountPercent = '66';

        if (subscrUK) {
          ids = {
            one: environment === Environment.prod ? 156 : 161,
            two: environment === Environment.prod ? 157 : 162,
            three: environment === Environment.prod ? 158 : 163,
          };
        } else if (subscrEU) {
          ids = {
            one: environment === Environment.prod ? 159 : 164,
            two: environment === Environment.prod ? 160 : 165,
            three: environment === Environment.prod ? 161 : 166,
          };
        } else {
          ids = {
            one: environment === Environment.prod ? 153 : 158,
            two: environment === Environment.prod ? 154 : 159,
            three: environment === Environment.prod ? 155 : 160,
          };
        }

        title = {
          one: oneMonth.week,
          two: threeMonth.week,
          three: sixMonths.week,
        };
        defaultPrices = {
          one: '11.99',
          two: '21.99',
          three: '32.99',
        };
        defaultPricesPerDay = {
          one: 0.39,
          two: 0.24,
          three: 0.18,
        };
        defaultPricesPerWeek = {
          one: 2.79,
          two: 1.71,
          three: 1.28,
        };
        productName = {
          one: `${currency}11.99 per 1m intro, then ${currency}39.99 per 2 weeks`,
          two: `${currency}21.99 per 3m intro, then ${currency}49.99 per 1 month`,
          three: `${currency}32.99 per 6m intro, then ${currency}49.99 per 1 month`,
        };

        defaultOldPricesForPeriod = {
          one: getPriceBeforeDiscount(defaultPricesPerWeek.one, discountPercent),
          two: getPriceBeforeDiscount(defaultPricesPerWeek.two, discountPercent),
          three: getPriceBeforeDiscount(defaultPricesPerWeek.three, discountPercent),
        };
        defaultOldPricesPerDay = {
          one: getPriceBeforeDiscount(defaultPricesPerDay.one, discountPercent),
          two: getPriceBeforeDiscount(defaultPricesPerDay.two, discountPercent),
          three: getPriceBeforeDiscount(defaultPricesPerDay.three, discountPercent),
        };
        defaultOldPrices = {
          one: getPriceBeforeDiscount(defaultPrices.one, discountPercent),
          two: getPriceBeforeDiscount(defaultPrices.two, discountPercent),
          three: getPriceBeforeDiscount(defaultPrices.three, discountPercent),
        };

        break;

      //p31
      case PAYMENT_TARIFF.V49:
        if (subscrUK) {
          ids = {
            one: environment === Environment.prod ? 165 : 170,
            two: environment === Environment.prod ? 166 : 171,
            three: environment === Environment.prod ? 167 : 172,
          };
        } else if (subscrEU) {
          ids = {
            one: environment === Environment.prod ? 168 : 173,
            two: environment === Environment.prod ? 169 : 174,
            three: environment === Environment.prod ? 170 : 175,
          };
        } else {
          ids = {
            one: environment === Environment.prod ? 162 : 167,
            two: environment === Environment.prod ? 163 : 168,
            three: environment === Environment.prod ? 164 : 169,
          };
        }

        defaultPrices = {
          one: '1.99',
          two: '6.99',
          three: '14.99',
        };
        title = {
          one: oneWeek.week,
          two: oneMonth.week,
          three: threeMonth.week,
        };
        defaultPricesPerDay = {
          one: 0.28,
          two: 0.23,
          three: 0.16,
        };
        defaultPricesPerWeek = {
          one: 1.99,
          two: 1.63,
          three: 1.16,
        };
        productName = {
          one: `${currency}1.99 per 1w intro, then ${currency}29.99 per 2 weeks`,
          two: `${currency}6.99 per 1m intro, then ${currency}29.99 per 2 weeks`,
          three: `${currency}14.99 per 3m intro, then ${currency}49.99 per 1 month`,
        };

        // old prices 
        defaultOldPricesForPeriod = {
          one: '10.62',
          two: '5.94',
          three: '4.30',
        };
        defaultOldPricesPerDay = {
          one: '1.06',
          two: '0.60',
          three: '0.45',
        };
        defaultOldPrices = {
          one: '14.87',
          two: '62.82',
          three: '68.40',
        };

        break;

    default:
      ids = {
        month: environment === Environment.prod ? 9 : 12,
        threeMonths: environment === Environment.prod ? 8 : 15,
        sixMonts: environment === Environment.prod ? 7 : 14,
      };
      defaultPrices = {
        month: '29.99',
        threeMonths: '39.99',
        sixMonts: '49.99',
      };
      defaultPricesPerDay = {
        month: 0.99,
        threeMonths: 0.44,
        sixMonts: 0.28,
      };
      defaultPricesPerWeek = {
        month: 7.0,
        threeMonths: 3.11,
        sixMonts: 1.94,
      };
      defaultOldPrices = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesForPeriod = {
        one: '',
        two: '',
        three: '',
      };
      defaultOldPricesPerDay = {
        one: '',
        two: '',
        three: '',
      };
  }

  // Default discount handling:
  // - every non-special tariff shows DEFAULT_DISCOUNT (53% for K9 outside its p20/p29
  //   paywalls, 60% otherwise);
  // - the scratch-card discount screen (?p20v1DiscountScreenScratch=true) shows
  //   SCRATCH_DISCOUNT (61%) on every tariff;
  // - special-offer tariffs keep their own (higher) discount unless scratch is active.
  // When a discount is applied here we recalculate the strike-through weekly prices so
  // the displayed "X% off" stays consistent with the old price.
  let appliedDiscount: number | undefined;
  if (scratchDiscountScreen) {
    appliedDiscount = SCRATCH_DISCOUNT;
  } else if (!isSpecialOfferTariff(tariff) && discountPercent === undefined) {
    appliedDiscount = DEFAULT_DISCOUNT;
  }
  if (appliedDiscount !== undefined) {
    discountPercent = String(appliedDiscount);
    if (defaultPricesPerWeek.one != null) {
      defaultOldPricesForPeriod = {
        one: getPriceBeforeDiscount(defaultPricesPerWeek.one, appliedDiscount),
        two: getPriceBeforeDiscount(defaultPricesPerWeek.two, appliedDiscount),
        three: getPriceBeforeDiscount(defaultPricesPerWeek.three, appliedDiscount),
      };
    }
  }

  if (tariff && (tariff !== previousTariff || !isContentIdsCalled)) {
    getContentIds(ids);
    isContentIdsCalled = true;
    previousTariff = tariff;
  }

  const result: PricesConfig = {
    extraOptions: {
      mostPopularTitle: mostPopularTitle,
      discountPercent,
    },
    defaultDesign: {
      firstPriceConfig: {
        id: ids.year,
        title: oneYear.title,
        perFirst: oneYear.perFirst,
        perSecond: oneYear.perSecond,
        defaultPrice: defaultPrices.year,
        defaultPricePerDay: defaultPricesPerDay.year,
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.one || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.one || '',
      },
      secondPriceConfig: {
        id: ids.month,
        title: oneMonth.title,
        perFirst: oneMonth.perFirst,
        perSecond: oneMonth.perSecond,
        defaultPrice: defaultPrices.month,
        defaultPricePerDay: defaultPricesPerDay.month,
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.two || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.two || '',
      },
      thirdPriceConfig: {
        id: ids.week,
        title: oneWeek.title,
        perFirst: oneWeek.perFirst,
        perSecond: oneWeek.perSecond,
        defaultPrice: defaultPrices.week,
        defaultPricePerDay: defaultPricesPerDay.week,
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.three || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.three || '',
      },
    },
    everydoggyV1Design: {
      firstPriceConfig: {
        id: ids.month || 8,
        title: oneMonth.title,
        perWeek: oneMonth.perWeek,
        defaultPrice: defaultPrices.month,
        defaultPricePerDay: defaultPricesPerDay.month,
        defaultPricePerWeek: defaultPricesPerWeek.month || '6.99',
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.one || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.one || '',
      },
      secondPriceConfig: {
        id: ids.threeMonths || 10,
        title: threeMonth.title,
        perWeek: threeMonth.perWeek,
        defaultPrice: defaultPrices.threeMonths,
        defaultPricePerDay: defaultPricesPerDay.threeMonths,
        defaultPricePerWeek: defaultPricesPerWeek.threeMonths || '2.99',
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.two || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.two || '',
      },
      thirdPriceConfig: {
        id: ids.sixMonts || 11,
        title: sixMonths.title,
        perWeek: sixMonths.perWeek,
        defaultPrice: defaultPrices.sixMonts,
        defaultPricePerDay: defaultPricesPerDay.sixMonts,
        defaultPricePerWeek: defaultPricesPerWeek.sixMonts || '1.99',
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.three || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.three || '',
      },
    },
    everydoggyV2Design: {
      firstPriceConfig: {
        id: ids.year || 9,
        title: oneYear.title,
        titleWeek: oneYear.week,
        perDay: oneYear.perSecond,
        defaultPrice: defaultPrices.year,
        defaultPricePerDay: defaultPricesPerDay.year,
        defaultOldPrices: defaultOldPrices?.year || null,
        productName: productName?.year || '',
        defaultPricesPerWeek: defaultPricesPerWeek.year || '',
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.one || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.one || '',
      },
      secondPriceConfig: {
        id: ids.month || 8,
        title: oneMonth.title,
        titleWeek: oneMonth.week,
        perDay: oneMonth.perSecond,
        defaultPrice: defaultPrices.month,
        defaultPricePerDay: defaultPricesPerDay.month,
        defaultOldPrices: defaultOldPrices?.month || null,
        productName: productName?.month || '',
        defaultPricesPerWeek: defaultPricesPerWeek.month || '',
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.two || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.two || '',
      },
      thirdPriceConfig: {
        id: ids.threeMonths || 10,
        title: threeMonth.title,
        titleWeek: threeMonth.week,
        perDay: threeMonth.perSecond,
        defaultPrice: defaultPrices.threeMonths,
        defaultPricePerDay: defaultPricesPerDay.threeMonths,
        defaultOldPrices: defaultOldPrices?.threeMonths || null,
        productName: productName?.threeMonths || '',
        defaultPricesPerWeek: defaultPricesPerWeek.threeMonths || '',
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.three || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.three || '',
      },
    },
    everydoggyV4Design: {
      firstPriceConfig: {
        id: ids.week || 9,
        title: oneWeek.title,
        titleWeek:
          tariff === PAYMENT_TARIFF.V17 || tariff === PAYMENT_TARIFF.V18 || PAYMENT_TARIFF.V19 || PAYMENT_TARIFF.V20
            ? locales[lang].payment_text_109
            : oneYear.week,
        perDay: oneYear.perSecond,
        defaultPrice: defaultPrices.week,
        defaultPricePerDay: defaultPricesPerDay.week,
        defaultOldPrices: defaultOldPrices?.week || null,
        productName: productName?.week || '',
        defaultPricesPerWeek: defaultPricesPerWeek.week || '',
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.one || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.one || '',
      },
      secondPriceConfig: {
        id: ids.month || 8,
        title: oneMonth.title,
        titleWeek: oneMonth.week,
        perDay: oneMonth.perSecond,
        defaultPrice: defaultPrices.month,
        defaultPricePerDay: defaultPricesPerDay.month,
        defaultOldPrices: defaultOldPrices?.month || null,
        productName: productName?.month || '',
        defaultPricesPerWeek: defaultPricesPerWeek.month || '',
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.two || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.two || '',
      },
      thirdPriceConfig: {
        id: ids.threeMonths || 10,
        title: threeMonth.title,
        titleWeek: threeMonth.week,
        perDay: threeMonth.perSecond,
        defaultPrice: defaultPrices.threeMonths,
        defaultPricePerDay: defaultPricesPerDay.threeMonths,
        defaultOldPrices: defaultOldPrices?.threeMonths || null,
        productName: productName?.threeMonths || '',
        defaultPricesPerWeek: defaultPricesPerWeek.threeMonths || '',
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.three || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.three || '',
      },
    },
    everydoggyV6Design: {
      firstPriceConfig: {
        id: ids.sixMonts || 11,
        title: sixMonths.title,
        titleWeek: sixMonths.week,
        perDay: sixMonths.perSecond,
        defaultPrice: defaultPrices.sixMonts,
        defaultPricePerDay: defaultPricesPerDay.sixMonts,
        defaultOldPrices: defaultOldPrices?.sixMonts || null,
        productName: productName?.sixMonts || '',
        defaultPricesPerWeek: defaultPricesPerWeek.sixMonts || '',
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.one || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.one || '',
      },
      secondPriceConfig: {
        id: ids.month || 8,
        title: oneMonth.title,
        titleWeek: oneMonth.week,
        perDay: oneMonth.perSecond,
        defaultPrice: defaultPrices.month,
        defaultPricePerDay: defaultPricesPerDay.month,
        defaultOldPrices: defaultOldPrices?.month || null,
        productName: productName?.month || '',
        defaultPricesPerWeek: defaultPricesPerWeek.month || '',
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.two || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.two || '',
      },
      thirdPriceConfig: {
        id: ids.threeMonths || 10,
        title: threeMonth.title,
        titleWeek: threeMonth.week,
        perDay: threeMonth.perSecond,
        defaultPrice: defaultPrices.threeMonths,
        defaultPricePerDay: defaultPricesPerDay.threeMonths,
        defaultOldPrices: defaultOldPrices?.threeMonths || null,
        productName: productName?.threeMonths || '',
        defaultPricesPerWeek: defaultPricesPerWeek.threeMonths || '',
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.three || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.three || '',
      },
    },
  };

  if (
    tariff === PAYMENT_TARIFF.V21 ||
    tariff === PAYMENT_TARIFF.V22 ||
    tariff === PAYMENT_TARIFF.V27 ||
    tariff === PAYMENT_TARIFF.V28 ||
    tariff === PAYMENT_TARIFF.V29 ||
    tariff === PAYMENT_TARIFF.V30 ||
    tariff === PAYMENT_TARIFF.V31 ||
    tariff === PAYMENT_TARIFF.V32 ||
    tariff === PAYMENT_TARIFF.V33 ||
    tariff === PAYMENT_TARIFF.V34 ||
    tariff === PAYMENT_TARIFF.V35 ||
    tariff === PAYMENT_TARIFF.V36 ||
    tariff === PAYMENT_TARIFF.V37 ||
    tariff === PAYMENT_TARIFF.V38 ||
    tariff === PAYMENT_TARIFF.V39 ||
    tariff === PAYMENT_TARIFF.V40 ||
    tariff === PAYMENT_TARIFF.V41 ||
    tariff === PAYMENT_TARIFF.V42 ||
    tariff === PAYMENT_TARIFF.V43 ||
    tariff === PAYMENT_TARIFF.V44 ||
    tariff === PAYMENT_TARIFF.V45 ||
    tariff === PAYMENT_TARIFF.V46 ||
    tariff === PAYMENT_TARIFF.V47 ||
    tariff === PAYMENT_TARIFF.V48 ||
    tariff === PAYMENT_TARIFF.V49 ||
    tariff === PAYMENT_TARIFF.V001 ||
    tariff === PAYMENT_TARIFF.V002 ||
    tariff === PAYMENT_TARIFF.V003
  ) {
    result.everydoggyNewDesign = {
      firstPriceConfig: {
        id: ids.one || 58,
        title: title.one,
        titleWeek: title.one,
        perDay: oneYear.perSecond,
        defaultPrice: defaultPrices.one,
        defaultPricePerDay: defaultPricesPerDay.one,
        defaultOldPrices: defaultOldPrices?.one || null,
        productName: productName?.one || '',
        defaultPricesPerWeek: defaultPricesPerWeek.one || '',
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.one || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.one || '',
      },
      secondPriceConfig: {
        id: ids.two || 59,
        title: title.two,
        titleWeek: title.two,
        perDay: oneMonth.perSecond,
        defaultPrice: defaultPrices.two,
        defaultPricePerDay: defaultPricesPerDay.two,
        defaultOldPrices: defaultOldPrices?.two || null,
        productName: productName?.two || '',
        defaultPricesPerWeek: defaultPricesPerWeek.two || '',
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.two || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.two || '',
      },
      thirdPriceConfig: {
        id: ids.three || 60,
        title: title.three,
        titleWeek: title.three,
        perDay: threeMonth.perSecond,
        defaultPrice: defaultPrices.three,
        defaultPricePerDay: defaultPricesPerDay.three,
        defaultOldPrices: defaultOldPrices?.three || null,
        productName: productName?.three || '',
        defaultPricesPerWeek: defaultPricesPerWeek.three || '',
        defaultOldPricesForPeriod: defaultOldPricesForPeriod.three || '',
        defaultOldPricesPerDay: defaultOldPricesPerDay.three || '',
      },
    };
  }

  return result;
}
